import { updateObject } from 'utils/updateObject'
import setFiltersUtil from 'store/reducer-utils/setFilters'
import omit from 'lodash/omit'
import {
  DROP_ALL_DATA,
  DROP_ALL_ITEMS,
  DROP_DATA,
  MERGE_STATISTICS,
  SET_COUNTER,
  SET_DATA,
  SET_FILTERS,
  SET_PAGE,
  SET_RESOURCE,
  SET_SELECTION,
  SET_SORTING,
  TOGGLE_GROUP_PROCESSING,
  TOGGLE_LOADING,
  UPDATE_ITEM_BY_ID,
  UPDATE_ITEMS,
} from 'modules/Services/CampaignsEditor/types'
import { isArray } from 'lodash'

const initialResources = {
  items: [],
  summary: {},
  selection: { ids: {}, processing: [] },
  sorting: [
    {
      id: 'status',
      desc: false,
    },
  ],
  pagination: {
    page: 1,
    offset: 0,
    limit: 50,
    total: 0,
  },
}

const initialState = {
  groupProcessing: false,
  counters: {
    campaigns: null,
    'ad-groups': null,
    banners: null,
  },
  currentResource: null,
  loading: true,
  campaigns: initialResources,
  'ad-groups': initialResources,
  banners: initialResources,
  filters: null,
}

const setResource = (state, { payload }) =>
  updateObject(state, { currentResource: payload })

const setData = (
  state,
  { payload: { items = [], summary = {}, pagination = {}, update = false } },
) => {
  if (!state.currentResource) return state

  const currentResource = state[state.currentResource]

  return updateObject(state, {
    [state.currentResource]: {
      ...currentResource,
      items: update ? items : [...currentResource.items, ...items],
      summary: { ...currentResource.summary, ...summary },
      pagination: { ...currentResource.pagination, ...pagination },
    },
  })
}

const dropList = (state, { payload }) => {
  if (!state.currentResource) return state

  const updatedList = payload?.resource
    ? payload.resource
    : state.currentResource

  return updateObject(state, {
    [updatedList]: {
      ...initialResources,
      sorting: state[updatedList].sorting,
    },
  })
}

const dropAllItems = (state) => {
  return updateObject(state, {
    ...state,
    campaigns: initialResources,
    'ad-groups': initialResources,
    banners: initialResources,
  })
}

const mergeStatistics = (state, { payload: { items } }) => {
  const currentResource = state.currentResource
  if (!currentResource) {
    return
  }

  let current = [...state[currentResource].items]

  let nodeName

  switch (currentResource) {
    case 'campaigns':
      nodeName = 'campaign'
      break
    case 'ad-groups':
      nodeName = 'banner'
      break
    case 'banners':
      nodeName = 'banner'
      break
  }

  items.map((item) => {
    const index = current.findIndex(
      (i) => i.external_id === item[nodeName]?.external_id,
    )

    if (index > -1) {
      current[index] = { ...current[index], ...omit(item, [nodeName]) }
    }
  })

  return updateObject(state, {
    [state.currentResource]: {
      ...state[currentResource],
      items: current,
    },
  })
}

const toggleLoading = (state, { payload }) =>
  updateObject(state, { loading: payload ?? !state.loading })

const toggleGroupProcessing = (state, { payload }) =>
  updateObject(state, { groupProcessing: payload ?? state.groupProcessing })

const setCounter = (state, { payload }) =>
  updateObject(state, {
    ...state,
    counters: {
      ...state.counters,
      ...payload,
    },
  })

const setFilters = (state, action) => {
  const filteredPayload = state?.filters
    ? [...action.payload].filter(
        (filter) => Object.keys(state.filters).indexOf(filter.fieldName) > -1,
      )
    : action.payload

  return setFiltersUtil(state, { ...action, payload: filteredPayload })
}

const setSelections = (state, { payload }) => {
  if (!state.currentResource) return state

  const currentResource = payload.resource ?? state.currentResource

  return updateObject(state, {
    [currentResource]: {
      ...state[currentResource],
      selection: {
        ...state[currentResource].selection,
        ids: payload.ids,
      },
    },
  })
}

const setSorting = (state, { payload }) => {
  if (!state.currentResource) return state

  const currentResource = state.currentResource

  return updateObject(state, {
    [currentResource]: {
      ...state[currentResource],
      sorting: payload,
    },
  })
}

const setPage = (state, { payload }) => {
  if (!state.currentResource) return state

  const currentResource = state[state.currentResource]

  return updateObject(state, {
    [state.currentResource]: {
      ...currentResource,
      pagination: {
        ...currentResource.pagination,
        page: payload,
      },
    },
  })
}

const updateItemById = (state, { payload }) => {
  if (!state.currentResource) return state

  const currentResource = state[state.currentResource]
  const updatedItems = [...currentResource.items]

  const updates = !isArray(payload) ? [payload] : payload

  updates.forEach((item) => {
    const index = currentResource.items.findIndex(
      (i) => i.external_id === item.external_id,
    )

    if (index > -1) {
      updatedItems[index] = { ...updatedItems[index], ...item }
    }
  })

  return updateObject(state, {
    [state.currentResource]: {
      ...currentResource,
      items: updatedItems,
    },
  })
}

const dropAllData = (state) => {
  return updateObject(state, { ...initialState })
}

const reducer = (state = initialState, action) => {
  switch (action.type) {
    case SET_RESOURCE:
      return setResource(state, action)
    case TOGGLE_LOADING:
      return toggleLoading(state, action)
    case UPDATE_ITEMS:
      return setData(state, {
        ...action,
        payload: { ...action.payload, update: true },
      })
    case SET_DATA:
      return setData(state, action)
    case SET_FILTERS:
      return setFilters(state, action)
    case MERGE_STATISTICS:
      return mergeStatistics(state, action)
    case SET_SELECTION:
      return setSelections(state, action)
    case SET_SORTING:
      return setSorting(state, action)
    case SET_COUNTER:
      return setCounter(state, action)
    case SET_PAGE:
      return setPage(state, action)
    case UPDATE_ITEM_BY_ID:
      return updateItemById(state, action)
    case DROP_DATA:
      return dropList(state, action)
    case DROP_ALL_ITEMS:
      return dropAllItems(state)
    case DROP_ALL_DATA:
      return dropAllData(state)
    case TOGGLE_GROUP_PROCESSING:
      return toggleGroupProcessing(state, action)
    default:
      return state
  }
}

export default reducer
